// src/components/chat/AgentSelector.tsx
import * as React from "react";
import { createLogger } from "../../utils/logger";
import { track } from "../../utils/analytics";

const log = createLogger("AgentSelector");

/**
 * Enhanced Agent interface with flexible icon support
 *
 * DESIGN PRINCIPLE: The interface remains generic and reusable while
 * supporting optional icon functionality. The parent component handles
 * icon imports and passes them as React components to this interface.
 *
 * The icon property accepts any React component, making it flexible
 * enough to work with different SVG component signatures while
 * maintaining type safety.
 */
export interface Agent {
  id: string;
  name: string;
  description: string;
  // Optional icon component - flexible to handle different SVG signatures
  icon?: React.ComponentType<any>;
  isDisabled?: boolean; // Add this optional property
  tooltip?: string; // Optional tooltip text to show on hover
}

/**
 * Props interface for AgentSelector component
 */
interface AgentSelectorProps {
  currentAgent: Agent;
  agents: Agent[];
  onSelectAgent: (agent: Agent) => void;
  className?: string;
}

/**
 * Clean, focused AgentSelector component
 *
 * DESIGN PHILOSOPHY: This component follows the same robust patterns as
 * SettingsMenuButton for click-outside detection and state management,
 * but maintains a simpler visual design as requested.
 *
 * KEY FEATURES:
 * - Robust click-outside detection using containerRef pattern
 * - Clean visual design with just text and dropdown arrow
 * - Checkmark indicators in dropdown menu for selection state
 * - Comprehensive logging for debugging
 * - No unnecessary visual flourishes or borders
 */
const AgentSelector: React.FC<AgentSelectorProps> = ({
  currentAgent,
  agents,
  onSelectAgent,
  className = "",
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  // Container ref for reliable click-outside detection
  const containerRef = React.useRef<HTMLDivElement>(null);

  /**
   * Click-outside detection to close dropdown
   *
   * This implements the same robust pattern as SettingsMenuButton
   * to ensure the dropdown closes when users click elsewhere.
   */
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        log.debug("Click outside agent selector, closing menu");
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isMenuOpen]);

  /**
   * Handle agent selection
   *
   * Unlike SettingsMenuButton where multiple options can be toggled,
   * agent selection is exclusive, so we close the menu after selection.
   */
  const handleSelectAgent = (agent: Agent) => {
    log.info("Agent selected", {
      previousAgent: currentAgent.name,
      newAgent: agent.name,
      agentId: agent.id,
    });

    track("chat:agent_switched", {
      from_agent_id: currentAgent.id,
      from_agent_name: currentAgent.name,
      to_agent_id: agent.id,
      to_agent_name: agent.name,
    });

    onSelectAgent(agent);
    setIsMenuOpen(false);
  };

  /**
   * Toggle dropdown menu
   */
  const toggleMenu = () => {
    const newState = !isMenuOpen;
    log.debug("Agent menu toggle", {
      from: isMenuOpen ? "open" : "closed",
      to: newState ? "open" : "closed",
    });
    setIsMenuOpen(newState);
  };

  /**
   * Render agent icon component safely
   *
   * SAFETY PATTERN: This function handles cases where an agent might not
   * have an icon defined, ensuring the component remains robust and doesn't
   * break if some agents are configured without icons.
   *
   * FLEXIBILITY: The size parameter allows us to render icons at different
   * sizes for different contexts (e.g., smaller in dropdowns, larger in
   * other UI areas).
   */
  const renderAgentIcon = (agent: Agent, size: number = 20) => {
    if (!agent.icon) {
      return null; // Gracefully handle agents without icons
    }

    const IconComponent = agent.icon;
    return (
      <IconComponent
        width={size}
        height={size}
        style={{ flexShrink: 0 }} // Prevent icon from being compressed in flex layouts
      />
    );
  };
  const ActiveCheckmarkIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <polyline
        points="20,6 9,17 4,12"
        stroke="#000000"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <div ref={containerRef} style={{ position: "relative" }} className={className}>
      {/* Simple agent selector button - no borders or hover backgrounds */}
      <div
        onClick={toggleMenu}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          padding: "8px 12px",
          cursor: "pointer",
          userSelect: "none",
          fontFamily: "'Segoe UI', sans-serif",
        }}
        title={`Current agent: ${currentAgent.name}`}
        aria-label="Select AI agent"
      >
        {/* Agent name - simple text display */}
        <span
          style={{
            fontSize: "14px",
            color: "#333",
            fontWeight: 400,
          }}
        >
          {currentAgent.name}
        </span>

        {/* Dropdown arrow with rotation animation */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            transform: isMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="#B1B1B1"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Dropdown menu - positioned ABOVE like working examples */}
      {isMenuOpen && (
        <div
          style={{
            position: "absolute",
            bottom: "100%", // Position ABOVE the trigger like SettingsMenuButton
            right: "-30px", // Align with right edge of trigger
            marginBottom: "2px", // Space above the trigger
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            border: "1px solid #e0e0e0",
            minWidth: "220px",
            zIndex: 1000, // Same as working examples
            overflow: "hidden",
            fontFamily: "'Segoe UI', sans-serif",
          }}
          onClick={(e) => {
            e.stopPropagation();
            log.info("Dropdown clicked - menu is visible and interactive");
          }}
        >
          {agents.map((agent, index) => {
            const isSelected = agent.id === currentAgent.id;

            return (
              <button
                key={agent.id}
                onClick={() => !agent.isDisabled && handleSelectAgent(agent)} // Prevent click if disabled
                title={agent.tooltip} // Add tooltip text on hover
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  backgroundColor: "transparent",
                  border: "none",
                  cursor: agent.isDisabled ? "not-allowed" : "pointer", // Change cursor for disabled
                  transition: "background-color 0.2s ease",
                  borderBottom: index < agents.length - 1 ? "1px solid #f0f0f0" : "none",
                  textAlign: "left",
                  opacity: agent.isDisabled ? 0.5 : 1, // Gray out disabled agents
                }}
                onMouseEnter={(e) => {
                  if (!agent.isDisabled) {
                    // Only hover effect if not disabled
                    (e.currentTarget as HTMLElement).style.backgroundColor = "#f9f9f9";
                  }
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
                }}
              >
                {/* Enhanced agent option with icon, text, and selection indicator */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    flex: 1,
                    pointerEvents: "none",
                  }}
                >
                  {/* Agent icon - distinctive visual identifier */}
                  <div style={{ flexShrink: 0 }}>{renderAgentIcon(agent, 24)}</div>

                  {/* Agent name and description */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                      overflow: "hidden",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#333",
                        fontWeight: isSelected ? 500 : 400,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {agent.name}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {agent.description}
                    </span>
                  </div>
                </div>

                {/* Selection indicator - only show checkmark when selected */}
                <div style={{ pointerEvents: "none", paddingLeft: "8px" }}>
                  {isSelected && <ActiveCheckmarkIcon />}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AgentSelector;
